import React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Monitor, XCircle } from "lucide-react";
import { MonitorDetectionResult } from "@/lib/monitorDetection";
import { MonitorDetectionAction } from "@/types/video-call";

interface MonitoringViolationDialogProps {
  showWarning: boolean;
  showBlock: boolean;
  monitorResult: MonitorDetectionResult | null;
  action: MonitorDetectionAction;
  onDismissWarning: () => void;
  onDismissBlock: () => void;
  onRetry?: () => void;
}

export const MonitoringViolationDialog: React.FC<MonitoringViolationDialogProps> = ({
  showWarning,
  showBlock,
  monitorResult,
  action,
  onDismissWarning,
  onDismissBlock,
  onRetry,
}) => {
  // Warning Dialog
  if (showWarning && action === "warning") {
    return (
      <Dialog open={showWarning} onOpenChange={onDismissWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="w-6 h-6 mr-2 text-amber-600" />
              Multiple Monitors Detected
            </DialogTitle>
            <DialogDescription>
              We've detected multiple displays connected to your system.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {monitorResult && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Detection Details:
                  </p>
                  <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                    <li>• {monitorResult.screenCount} displays detected</li>
                    <li>• Detection method: {monitorResult.detectionMethod}</li>
                    <li>• Confidence: {Math.round(monitorResult.confidence * 100)}%</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2">For proctoring compliance, please:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Disconnect all external monitors</li>
                <li>Turn off screen mirroring</li>
                <li>Disable extended desktop mode</li>
                <li>Close virtual display software</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={onDismissWarning}>
              I'll Continue Anyway
            </Button>
            {onRetry && (
              <Button variant="default" onClick={onRetry}>
                Check Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Block Dialog
  if (showBlock && action === "block") {
    return (
      <Dialog open={showBlock} onOpenChange={onDismissBlock}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <XCircle className="w-6 h-6 mr-2 text-red-600" />
              Access Blocked
            </DialogTitle>
            <DialogDescription>
              Multiple monitors detected. Please resolve this before continuing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {monitorResult && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    Security Violation Details:
                  </p>
                  <ul className="text-xs text-red-700 dark:text-red-300 space-y-1">
                    <li>• {monitorResult.screenCount} displays detected</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2 font-medium">Required Actions:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Physically disconnect all external monitors</li>
                <li>Unplug HDMI, USB-C, and wireless display adapters</li>
                <li>Close virtual display software (TeamViewer, etc.)</li>
                <li>Disable screen mirroring and extended desktop</li>
                <li>Restart your computer if necessary</li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <div className="flex items-start">
                <Monitor className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
                    Single Monitor Only
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    Proctoring requires a single display setup. Once you've disconnected all external 
                    displays, click "Check Again" to verify compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onDismissBlock}>
              Cancel Session
            </Button>
            {onRetry && (
              <Button onClick={onRetry}>
                Check Again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return null;
};